/**
 * E03-F05-T04 — Document editor + preview. Clone of the legacy
 * openDocEditor / openDocPreview / _togglePreviewEdit / regenerateDocument:
 *
 *   - per-type edit forms (renderDocEditForm) built from DOC_EDIT_FORMS;
 *   - Этап 4 / KS-07: prose fields are edited in the shared TipTap RichTextEditor,
 *     which REPLACED the B/I/U toolbar that wrapped a textarea selection in
 *     `**`/`*`/`__`. Markdown could not express colour, and the marker toolbar had
 *     no way to offer it; the editor carries its own toolbar, so the document-level
 *     one is gone rather than duplicated;
 *   - `kind:'list'` fields stay PLAIN textareas: their contract is one item per line
 *     (saved via a newline split), and a rich editor would turn each line into a
 *     `<p>` and break that round-trip;
 *   - JD `*_comments` rendered read-only (grey) — never edited, saved as-is;
 *   - View mode renders through DocumentView;
 *   - a regenerate dialog with an `instruction` field + general notes (pre-filled
 *     from generation_notes); KS/FU use their own ai action, the four core docs use
 *     regenerate_document;
 *   - the actions Save (edit mode), Approve (not yet approved), Send
 *     (fast-track + approved) — in a sticky TOP bar, not a bottom footer
 *     (E10-F04-T09), so they stay visible without scrolling.
 *
 * State is local; persistence goes through the typed mutations (docGenerationApi).
 */
import { useMemo, useState, type JSX } from 'react';
import { Dialog, DialogFooter } from '../ui/Dialog.js';
import { useToast } from '../ui/ToastContext.js';
import {
  artifactStatusOf,
  badgeFor,
  editFieldsForContent,
  previewFooterActions,
  valueAtPath,
  withValueAtPath,
  type ArtifactLike,
  type DocEditField,
} from './docGenerationModel.js';
import {
  useApproveArtifact,
  useGenerateFollowupEmail,
  useGenerateKickoffSheet,
  useRegenerateDocument,
  useSaveArtifact,
} from './docGenerationApi.js';
import {
  GENERATION_LABELS,
  REGENERATABLE_DOC_TYPES,
  type EditableDocType,
  type RegeneratableDocType,
} from '@eer/shared';
import { DocumentView } from './DocumentView.js';
import { RichTextEditor } from '../ui/RichTextEditor.js';
import { editorHtmlToListItems, listItemsToEditorHtml } from './listFieldModel.js';

/** Human doc-type label for the editor title (legacy docConfig.label). */
const DOC_TYPE_LABELS: Record<string, string> = {
  ...GENERATION_LABELS,
  sourcing_sheet: 'Sourcing Sheet',
};

/** Resolve the human label for a doc type (fallback: the raw type). */
function docTypeLabel(docType: string): string {
  return DOC_TYPE_LABELS[docType] ?? docType;
}

export interface DocumentEditorProps {
  readonly projectId: number;
  readonly docType: EditableDocType;
  /** The artifact being edited (its `content` seeds the form). */
  readonly artifact: ArtifactLike;
  /**
   * @deprecated The regenerate dialog was merged to a single "Regeneration
   * instructions" field (E03-F05-T09 / ISSUE-1); the separate general-notes field
   * is gone, so this prop is accepted for backward-compat but no longer read.
   */
  readonly generationNotes?: string;
  /** Fast-track project → the Send action appears on an approved comm doc. */
  readonly fastTrack?: boolean;
  /**
   * Open the send dialog for this doc (host owns the SendEmailDialog mount). When
   * omitted the Send button is not shown even if the footer would allow it.
   */
  readonly onSend?: (docType: EditableDocType) => void;
  /** Close the editor (host shell owns mount). */
  readonly onClose?: () => void;
}

/** Coerce a content value to the string the field edits. */
function fieldValue(content: Record<string, unknown>, field: DocEditField): string {
  // `field.key` may be a dotted path into a nested object (E03-F05-T13) — a
  // Kickoff Sheet carries a whole Sourcing Sheet under `sourcing_sheet`.
  const raw = valueAtPath(content, field.key);
  // KS2-02: список редактируется КАК СПИСОК в том же редакторе, что и остальные
  // поля — пункт становится `<li>`. Раньше пункты склеивались через перевод
  // строки в textarea, и после перехода генерации на HTML рекрутёр видел там теги.
  if (field.kind === 'list') return listItemsToEditorHtml(raw);
  return typeof raw === 'string' ? raw : raw == null ? '' : String(raw);
}

/** One editable InMail variant (kind:'variants') — held as an object, not a string. */
interface VariantDraft {
  readonly tone: string;
  readonly subject: string;
  readonly body: string;
}

/**
 * Coerce a content value into the structured variant array the editor edits
 * (kind:'variants'). Preserves tone/subject/body from each `{tone,subject,body}`
 * object; non-object entries degrade gracefully to empty fields.
 */
function variantValues(content: Record<string, unknown>, field: DocEditField): VariantDraft[] {
  const raw = valueAtPath(content, field.key);
  if (!Array.isArray(raw)) return [];
  return raw.map((x) => {
    const o = (x ?? {}) as { tone?: unknown; subject?: unknown; body?: unknown };
    return {
      tone: typeof o.tone === 'string' ? o.tone : '',
      subject: typeof o.subject === 'string' ? o.subject : '',
      body: typeof o.body === 'string' ? o.body : '',
    };
  });
}

/** Render one read-only comments block (grey list). */
function CommentsBlock({ value }: { value: unknown }): JSX.Element {
  const lines = Array.isArray(value)
    ? (value as unknown[]).map(String)
    : value
      ? [String(value)]
      : [];
  return (
    <div
      data-testid="doc-comments"
      className="rounded-md bg-[var(--color-surface-muted)] border border-[var(--color-border)] p-2 text-xs text-[var(--color-text-muted)]"
    >
      {lines.length === 0 ? (
        <em>No comments</em>
      ) : (
        <ul className="list-disc pl-4 space-y-0.5">
          {lines.map((l, i) => (
            <li key={i}>{l}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function DocumentEditor({
  projectId,
  docType,
  artifact,
  fastTrack = false,
  onSend,
  onClose,
}: DocumentEditorProps): JSX.Element {
  const toast = useToast();
  const initialContent = (artifact.content as Record<string, unknown>) ?? {};
  // E03-F05-T13: the form is derived from the CONTENT, not from a hardcoded key
  // list — so a section the view shows is never blank here just because the
  // generation prompt renamed its key.
  const fields = editFieldsForContent(docType, initialContent);

  // Editable per-field text state (list/markdown/text); comments/group/variants
  // are not staged here (variants use the structured slice below).
  const [draft, setDraft] = useState<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const f of fields) {
      if (f.kind !== 'comments' && f.kind !== 'group' && f.kind !== 'variants')
        out[f.key] = fieldValue(initialContent, f);
    }
    return out;
  });
  // Structured per-variant draft (kind:'variants', InMails) held AS OBJECTS, not
  // a flattened string — so tone/subject/body survive a Save round-trip intact
  // (E03-F05-T12). Keyed by field key, mirroring `draft`.
  const [variantsDraft, setVariantsDraft] = useState<Record<string, VariantDraft[]>>(() => {
    const out: Record<string, VariantDraft[]> = {};
    for (const f of fields) {
      if (f.kind === 'variants') out[f.key] = variantValues(initialContent, f);
    }
    return out;
  });
  const [editing, setEditing] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);
  const [instruction, setInstruction] = useState('');

  const status = artifactStatusOf({ [docType]: artifact }, docType);
  const badge = badgeFor(status);

  const save = useSaveArtifact(projectId);
  const approve = useApproveArtifact(projectId);
  const regen = useRegenerateDocument(projectId);
  const genKickoff = useGenerateKickoffSheet(projectId);
  const genFollowup = useGenerateFollowupEmail(projectId);

  const footer = useMemo(
    () => previewFooterActions(status, editing, fastTrack),
    [status, editing, fastTrack],
  );

  // The regenerate call is a real (slow, ~10-60s) AI call with no server-sent
  // progress. Unlike the card-level Regenerate (GenerationStageView's `cardBusy`
  // spinner), this dialog gave zero feedback while pending — a click looked like
  // it "did nothing" for the whole wait. Surface the same busy signal here.
  const regenerating = regen.isPending || genKickoff.isPending || genFollowup.isPending;

  /** Patch one part of one variant (kind:'variants'), keeping the object shape. */
  function updateVariant(
    key: string,
    index: number,
    part: keyof VariantDraft,
    value: string,
  ): void {
    setVariantsDraft((prev) => {
      const list = prev[key] ?? [];
      const next = list.map((v, i) => (i === index ? { ...v, [part]: value } : v));
      return { ...prev, [key]: next };
    });
  }

  /** Build the content object to persist (list → array; prose verbatim). */
  function buildContent(): Record<string, unknown> {
    // Written through `withValueAtPath` so a dotted field lands inside its
    // nested object instead of creating a literal "a.b" key (E03-F05-T13).
    let out: Record<string, unknown> = { ...initialContent };
    for (const f of fields) {
      if (f.kind === 'comments' || f.kind === 'group') continue; // not persisted here
      if (f.kind === 'variants') {
        // Write the structured `{tone,subject,body}` objects back AS-IS — never
        // split/join through a string (that would drop tone/subject and corrupt
        // the artifact so the next read renders empty variants). E03-F05-T12.
        out = withValueAtPath(
          out,
          f.key,
          (variantsDraft[f.key] ?? []).map((v) => ({
            tone: v.tone,
            subject: v.subject,
            body: v.body,
          })),
        );
        continue;
      }
      const v = draft[f.key] ?? '';
      out = withValueAtPath(out, f.key, f.kind === 'list' ? editorHtmlToListItems(v) : v);
    }
    return out;
  }

  async function onSave(): Promise<void> {
    try {
      await save.mutateAsync({ docType, content: buildContent() });
      toast.success('Document saved');
      setEditing(false);
    } catch {
      toast.error('Save failed');
    }
  }

  async function onApprove(): Promise<void> {
    try {
      await approve.mutateAsync(docType);
      toast.success('Document approved');
    } catch {
      toast.error('Approve failed');
    }
  }

  async function onRegenerate(): Promise<void> {
    try {
      // Single "Regeneration instructions" field (E03-F05-T09 / ISSUE-1). KS/FU
      // accept only a `generation_notes` channel, so the instruction is passed
      // there (previously it was a dead field for those two types); the core docs
      // take a real `instruction` param and no longer collect separate notes.
      if (docType === 'kickoff_sheet') {
        await genKickoff.mutateAsync(instruction || undefined);
      } else if (docType === 'client_followup_email') {
        await genFollowup.mutateAsync(instruction || undefined);
      } else if ((REGENERATABLE_DOC_TYPES as readonly string[]).includes(docType)) {
        await regen.mutateAsync({
          docType: docType as RegeneratableDocType,
          instruction: instruction || '',
          generationNotes: '',
        });
      }
      toast.success('Document regenerated');
      setRegenOpen(false);
      setInstruction('');
    } catch {
      toast.error('Regenerate failed');
    }
  }

  return (
    <div data-testid="document-editor" className="flex flex-col gap-3">
      {/* Header: title + status + Edit/View toggle + Regenerate + the action
          buttons (Save / Approve / Send / Close). E10-F04-T09: the actions used
          to sit in a footer BELOW the (long) form, so a recruiter edited, never
          scrolled down to Save and lost the edits silently. They now live in this
          bar, which is `sticky top-0` inside the dialog's scrolling body — always
          visible without scrolling, in both view and edit mode. */}
      <div
        data-testid="doc-actions"
        className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-2 border-b border-[var(--color-border)] bg-white pb-3"
      >
        <div className="flex items-center gap-2">
          <h3 data-testid="doc-title" className="font-semibold text-[var(--color-text-strong)]">
            {docTypeLabel(docType)}
          </h3>
          <span
            data-testid="doc-status"
            data-status={status}
            className={`text-xs px-2 py-0.5 rounded-full ${badge.toneClass}`}
          >
            {badge.label}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="doc-toggle-edit"
            onClick={() => setEditing((e) => !e)}
            className="min-h-[36px] px-3 rounded-md border border-[var(--color-border)] text-sm hover:bg-[var(--color-surface-muted)]"
          >
            {editing ? 'View' : 'Edit'}
          </button>
          <button
            type="button"
            data-testid="doc-open-regenerate"
            onClick={() => setRegenOpen(true)}
            className="min-h-[36px] px-3 rounded-md border border-[var(--color-border)] text-sm hover:bg-[var(--color-surface-muted)]"
          >
            Regenerate
          </button>
          {footer.showSave && (
            <button
              type="button"
              data-testid="doc-save"
              onClick={() => void onSave()}
              className="min-h-[36px] px-4 rounded-md bg-[var(--color-primary)] text-white text-sm font-medium hover:bg-[var(--color-primary-hover)]"
            >
              Save
            </button>
          )}
          {footer.showApprove && (
            <button
              type="button"
              data-testid="doc-approve"
              onClick={() => void onApprove()}
              className="min-h-[36px] px-4 rounded-md border border-[var(--color-success)] text-[var(--color-success)] text-sm font-medium hover:bg-[var(--color-success)]/10"
            >
              Approve
            </button>
          )}
          {footer.showSend && onSend && (
            <button
              type="button"
              data-testid="doc-send"
              onClick={() => onSend(docType)}
              className="min-h-[36px] px-4 rounded-md bg-[var(--color-accent)] text-[var(--color-primary)] text-sm font-medium hover:bg-[var(--color-accent-hover)]"
            >
              Send
            </button>
          )}
          {onClose && (
            <button
              type="button"
              data-testid="doc-close"
              onClick={onClose}
              className="min-h-[36px] px-4 rounded-md border border-[var(--color-border)] text-sm"
            >
              Close
            </button>
          )}
        </div>
      </div>

      {/* Per-type body: structured document view (read) or the edit form. */}
      {editing ? (
        <div className="flex flex-col gap-3">
          {fields.map((f) => {
            if (f.kind === 'comments') {
              return (
                <div key={f.key} className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-[var(--color-text-muted)]">
                    {f.label}
                  </span>
                  <CommentsBlock value={valueAtPath(initialContent, f.key)} />
                </div>
              );
            }
            if (f.kind === 'group') {
              return (
                <h4
                  key={f.key}
                  className="text-sm font-bold text-[var(--color-text-strong)] border-b border-[var(--color-border)] pb-1 mt-2"
                >
                  {f.label}
                </h4>
              );
            }
            if (f.kind === 'variants') {
              // One editable block PER variant (InMails) — Subject input + Body
              // textarea — so tone/subject/body are edited independently instead
              // of being flattened into a single blob (E03-F05-T12). The variant
              // count is fixed by generation (no add/remove controls).
              const variants = variantsDraft[f.key] ?? [];
              return (
                <div key={f.key} className="flex flex-col gap-3">
                  <span className="text-sm font-medium text-[var(--color-text-strong)]">
                    {f.label}
                  </span>
                  {variants.length === 0 ? (
                    <span className="text-xs text-[var(--color-text-muted)]">
                      No variants to edit.
                    </span>
                  ) : (
                    variants.map((v, i) => (
                      <div
                        key={i}
                        data-testid={`doc-field-variant-${i}`}
                        className="flex flex-col gap-2 rounded-md border border-[var(--color-border)] p-3"
                      >
                        <span className="text-xs font-semibold text-[var(--color-text-muted)]">
                          Variant {i + 1}
                          {v.tone ? ` · ${v.tone}` : ''}
                        </span>
                        <label className="flex flex-col gap-1 text-sm">
                          <span className="font-medium text-[var(--color-text-strong)]">
                            Subject
                          </span>
                          <input
                            type="text"
                            data-testid={`doc-field-variant-${i}-subject`}
                            className="border border-[var(--color-border)] rounded-md p-2 text-sm outline-none focus:border-[var(--color-accent)]"
                            value={v.subject}
                            onChange={(e) => updateVariant(f.key, i, 'subject', e.target.value)}
                          />
                        </label>
                        <label className="flex flex-col gap-1 text-sm">
                          <span className="font-medium text-[var(--color-text-strong)]">Body</span>
                          {/* InMail — это письмо кандидату, поэтому тот же редактор. */}
                          <RichTextEditor
                            testId={`doc-field-variant-${i}-body`}
                            ariaLabel={`Variant ${i + 1} body`}
                            minHeightClass="min-h-[160px]"
                            value={v.body}
                            onChange={(html) => updateVariant(f.key, i, 'body', html)}
                          />
                        </label>
                      </div>
                    ))
                  )}
                </div>
              );
            }
            const value = draft[f.key] ?? '';
            return (
              <div key={f.key} className="flex flex-col gap-1">
                <label
                  className="text-sm font-medium text-[var(--color-text-strong)]"
                  htmlFor={`doc-field-${f.key}`}
                >
                  {f.label}
                  {f.kind === 'list' && (
                    <span className="text-[var(--color-text-muted)] font-normal">
                      {' '}
                      (one per line)
                    </span>
                  )}
                </label>
                {f.kind === 'text' ? (
                  <input
                    id={`doc-field-${f.key}`}
                    data-testid={`doc-field-${f.key}`}
                    type="text"
                    className="border border-[var(--color-border)] rounded-md p-2 text-sm outline-none focus:border-[var(--color-accent)]"
                    value={value}
                    onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                  />
                ) : f.kind === 'richline' ? (
                  // Short value, SAME toolbar as every other field — only a
                  // shorter box. A bare <input> here is what made the form look
                  // half-formattable (E03-F05-T13).
                  <RichTextEditor
                    testId={`doc-field-${f.key}`}
                    ariaLabel={f.label}
                    minHeightClass="min-h-[64px]"
                    value={value}
                    onChange={(html) => setDraft((d) => ({ ...d, [f.key]: html }))}
                  />
                ) : f.kind === 'markdown' ? (
                  <RichTextEditor
                    testId={`doc-field-${f.key}`}
                    ariaLabel={f.label}
                    minHeightClass="min-h-[240px]"
                    value={value}
                    onChange={(html) => setDraft((d) => ({ ...d, [f.key]: html }))}
                  />
                ) : (
                  // kind:'list' — тот же редактор, пункты это элементы списка
                  // (`listFieldModel`). Прежняя textarea показывала пользователю
                  // `<p><b>…` сырым текстом, как только генерация перешла на HTML.
                  <RichTextEditor
                    testId={`doc-field-${f.key}`}
                    ariaLabel={f.label}
                    minHeightClass="min-h-[240px]"
                    value={value}
                    onChange={(html) => setDraft((d) => ({ ...d, [f.key]: html }))}
                  />
                )}
              </div>
            );
          })}
        </div>
      ) : (
        // Read-only preview MUST render the ORIGINAL server artifact, not
        // buildContent(): buildContent() constructs the SAVE payload and, for
        // `kind:'list'` fields, does `v.split('\n')` — which for inmails
        // `variants` (an array of {tone,subject,body} objects) collapses the
        // objects into bare strings, so the preview loses tone/subject/body
        // (E03-F05-T10). buildContent() is only for the Save click.
        <DocumentView docType={docType} content={initialContent} />
      )}

      {/* Regenerate dialog */}
      <Dialog
        open={regenOpen}
        onClose={() => setRegenOpen(false)}
        title="Regenerate document"
        testId="doc-regen-dialog"
        footer={
          <DialogFooter
            onCancel={() => setRegenOpen(false)}
            onConfirm={() => void onRegenerate()}
            confirmLabel={regenerating ? 'Regenerating…' : 'Regenerate'}
            confirmDisabled={regenerating}
            variant="accent"
            testId="doc-regen-footer"
          />
        }
      >
        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-[var(--color-text-strong)]">
              Regeneration instructions
            </span>
            <textarea
              data-testid="doc-regen-instruction"
              className="border border-[var(--color-border)] rounded-md p-2 text-sm min-h-[240px] outline-none focus:border-[var(--color-accent)]"
              placeholder="e.g. make the tone more formal, regenerate the whole document in Chinese…"
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
            />
          </label>
        </div>
      </Dialog>
    </div>
  );
}
